import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Collapse,
  Tooltip as MuiTooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import LinkIcon from '@mui/icons-material/Link';
import PushPinIcon from '@mui/icons-material/PushPin';
import { getDiagnosticStatusColor, getDiagnosticItemStatusColor } from '../utils/sampleResponses';

// Get status icon based on rule status
const StatusIcon = ({ status, size = 'small' }) => {
  const statusColor = getDiagnosticStatusColor(status);
  const iconProps = {
    fontSize: size,
    sx: { color: statusColor.text }
  };

  switch (status) {
    case 'PASSED':
      return <CheckCircleIcon {...iconProps} />;
    case 'FAILED':
      return <CancelIcon {...iconProps} />;
    case 'WARNING':
      return <WarningAmberIcon {...iconProps} />;
    case 'SKIPPED':
    default:
      return <RemoveCircleOutlineIcon {...iconProps} />;
  }
};

// Format detail key for display (camelCase to Title Case)
const formatDetailKey = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Diagnostic Header Component
const DiagnosticHeader = ({ diagnostic, colors, onDiagnosticAction }) => {
  const { title, subtitle, summary, actions } = diagnostic;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Title row with actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          {title}
        </Typography>
        {actions && actions.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {actions.slice(0, 2).map((action) => (
              <Button
                key={action.actionId}
                variant="outlined"
                size="small"
                onClick={() => onDiagnosticAction(action)}
                sx={{
                  borderColor: colors.border,
                  color: colors.textSecondary,
                  borderRadius: 0,
                  '&:hover': {
                    borderColor: '#22c55e',
                    color: '#22c55e',
                    bgcolor: 'rgba(34, 197, 94, 0.08)',
                  },
                }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {/* Subtitle */}
      {subtitle && (
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}

      {/* Summary badges */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip
            label={`${summary.totalItems} Items`}
            size="small"
            sx={{
              bgcolor: colors.surfaceElevated,
              color: colors.textSecondary,
              borderRadius: 0,
            }}
          />
          {summary.passed > 0 && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              label={`${summary.passed} Passed`}
              size="small"
              sx={{
                bgcolor: 'rgba(34, 197, 94, 0.15)',
                color: '#22c55e',
                borderRadius: 0,
                '& .MuiChip-icon': { color: '#22c55e' },
              }}
            />
          )}
          {summary.failed > 0 && (
            <Chip
              icon={<CancelIcon sx={{ fontSize: '14px !important' }} />}
              label={`${summary.failed} Failed`}
              size="small"
              sx={{
                bgcolor: 'rgba(239, 68, 68, 0.15)',
                color: '#ef4444',
                borderRadius: 0,
                '& .MuiChip-icon': { color: '#ef4444' },
              }}
            />
          )}
          {summary.warnings > 0 && (
            <Chip
              icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
              label={`${summary.warnings} Warnings`}
              size="small"
              sx={{
                bgcolor: 'rgba(245, 158, 11, 0.15)',
                color: '#f59e0b',
                borderRadius: 0,
                '& .MuiChip-icon': { color: '#f59e0b' },
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

// Next Move / Resolution Suggestion Component
const NextMoveBox = ({ nextMove, colors }) => {
  if (!nextMove) return null;

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      p: 1.5,
      mb: 2,
      bgcolor: 'rgba(34, 197, 94, 0.08)',
      border: '2px solid #22c55e',
      borderLeft: '4px solid #22c55e',
    }}>
      <TipsAndUpdatesIcon sx={{
        color: '#22c55e',
        fontSize: 24,
        mt: 0.25,
      }} />
      <Box>
        <Typography variant="caption" sx={{
          color: '#22c55e',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'block',
          mb: 0.25,
        }}>
          Suggested Resolution
        </Typography>
        <Typography variant="body2" sx={{
          color: colors.textPrimary,
          fontWeight: 500,
        }}>
          {nextMove}
        </Typography>
      </Box>
    </Box>
  );
};

// Related Entities Component
const RelatedEntities = ({ entities, colors, onRelatedEntityClick }) => {
  if (!entities || entities.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
      {entities.map((entity, index) => (
        <Chip
          key={`${entity.type}-${entity.id}-${index}`}
          label={entity.label}
          size="small"
          icon={<LinkIcon sx={{ fontSize: '12px !important' }} />}
          onClick={() => onRelatedEntityClick(entity)}
          sx={{
            bgcolor: 'rgba(59, 130, 246, 0.1)',
            color: '#3b82f6',
            borderRadius: 0,
            cursor: 'pointer',
            height: 22,
            '& .MuiChip-icon': { color: '#3b82f6' },
            '& .MuiChip-label': { fontSize: '0.7rem' },
            '&:hover': {
              bgcolor: 'rgba(59, 130, 246, 0.2)',
            },
          }}
        />
      ))}
    </Box>
  );
};

// Rule Actions Component
const RuleActions = ({ actions, colors, onRuleAction }) => {
  if (!actions || actions.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
      {actions.map((action) => (
        <Button
          key={action.actionId}
          variant="text"
          size="small"
          onClick={() => onRuleAction(action)}
          sx={{
            color: '#22c55e',
            p: 0.25,
            minWidth: 'auto',
            fontSize: '0.7rem',
            '&:hover': {
              bgcolor: 'rgba(34, 197, 94, 0.08)',
            },
          }}
        >
          {action.label}
        </Button>
      ))}
    </Box>
  );
};

// Single Rule Row Component
const RuleRow = ({ rule, colors, onRuleAction, onRelatedEntityClick }) => {
  const statusColor = getDiagnosticStatusColor(rule.status);

  return (
    <Box sx={{
      display: 'flex',
      gap: 1.5,
      py: 1,
      borderBottom: `1px solid ${colors.border}`,
      '&:last-child': { borderBottom: 'none' },
    }}>
      {/* Status icon */}
      <Box sx={{ pt: 0.25 }}>
        <StatusIcon status={rule.status} />
      </Box>

      {/* Rule content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Rule name and status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{
            color: colors.textPrimary,
            fontWeight: 500,
          }}>
            {rule.ruleName}
          </Typography>
          <Chip
            label={rule.status}
            size="small"
            sx={{
              bgcolor: statusColor.bg,
              color: statusColor.text,
              borderRadius: 0,
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
            }}
          />
        </Box>

        {/* Message */}
        <Typography variant="caption" sx={{
          color: rule.status === 'FAILED' ? statusColor.text : colors.textSecondary,
          display: 'block',
        }}>
          {rule.message}
        </Typography>

        {/* Details */}
        {rule.details && Object.keys(rule.details).length > 0 && (
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            mt: 0.5,
            pl: 1,
            borderLeft: `2px solid ${colors.border}`,
          }}>
            {Object.entries(rule.details).map(([key, value]) => (
              <Box key={key}>
                <Typography variant="caption" component="span" sx={{ color: colors.textMuted }}>
                  {formatDetailKey(key)}:
                </Typography>{' '}
                <Typography variant="caption" component="span" sx={{ color: colors.textSecondary }}>
                  {Array.isArray(value) ? value.join(', ') : String(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Related entities */}
        <RelatedEntities
          entities={rule.relatedEntities}
          colors={colors}
          onRelatedEntityClick={onRelatedEntityClick}
        />

        {/* Rule actions */}
        <RuleActions
          actions={rule.actions}
          colors={colors}
          onRuleAction={onRuleAction}
        />
      </Box>
    </Box>
  );
};

// Single Diagnostic Item Component
const DiagnosticItem = ({
  item,
  colors,
  onRuleAction,
  onRelatedEntityClick,
  defaultExpanded = false
}) => {
  const [expanded, setExpanded] = useState(item.expanded ?? defaultExpanded);
  const itemStatusColor = getDiagnosticItemStatusColor(item.status);

  return (
    <Box sx={{
      border: `1px solid ${colors.border}`,
      mb: 1,
      '&:last-child': { mb: 0 },
    }}>
      {/* Item header - clickable to expand/collapse */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          cursor: 'pointer',
          bgcolor: expanded ? colors.surface : colors.background,
          borderBottom: expanded ? `1px solid ${colors.border}` : 'none',
          '&:hover': {
            bgcolor: colors.surface,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
          {/* Expand/collapse icon */}
          <IconButton size="small" sx={{ p: 0 }}>
            {expanded ? (
              <ExpandLessIcon sx={{ color: colors.textSecondary }} />
            ) : (
              <ExpandMoreIcon sx={{ color: colors.textSecondary }} />
            )}
          </IconButton>

          {/* Item label and description */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{
              color: colors.textPrimary,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {item.label}
            </Typography>
            {item.description && (
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                {item.description}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Status badge */}
        <Chip
          label={item.status}
          size="small"
          sx={{
            bgcolor: itemStatusColor.bg,
            color: itemStatusColor.text,
            fontWeight: 600,
            borderRadius: 0,
            ml: 1,
          }}
        />
      </Box>

      {/* Expanded content - rules list */}
      <Collapse in={expanded}>
        <Box sx={{ p: 1.5, bgcolor: colors.surface }}>
          {/* Rules */}
          {item.rules?.map((rule) => (
            <RuleRow
              key={rule.ruleId}
              rule={rule}
              colors={colors}
              onRuleAction={onRuleAction}
              onRelatedEntityClick={onRelatedEntityClick}
            />
          ))}

          {/* Recommendation */}
          {item.recommendation && (
            <Box sx={{
              display: 'flex',
              gap: 1,
              mt: 1.5,
              p: 1,
              bgcolor: 'rgba(245, 158, 11, 0.1)',
              border: '1px dashed #f59e0b',
            }}>
              <LightbulbIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: colors.textPrimary }}>
                <strong>Recommendation:</strong> {item.recommendation}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// Main Diagnostic Renderer Component
const DiagnosticRenderer = ({
  diagnostic,
  colors,
  nextMove,
  onRuleAction,
  onDiagnosticAction,
  onRelatedEntityClick,
  onPinToBoard,
  sourceQuery,
}) => {
  if (!diagnostic) return null;

  const handleRuleAction = (action) => {
    if (onRuleAction) {
      onRuleAction(action.sentence);
    }
  };

  const handleDiagnosticAction = (action) => {
    if (onDiagnosticAction) {
      onDiagnosticAction(action.sentence);
    }
  };

  const handleRelatedEntityClick = (entity) => {
    if (onRelatedEntityClick) {
      onRelatedEntityClick(entity);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header with title, summary, and actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <DiagnosticHeader
            diagnostic={diagnostic}
            colors={colors}
            onDiagnosticAction={handleDiagnosticAction}
          />
        </Box>

        {/* Pin to Dashboard button */}
        {onPinToBoard && (
          <MuiTooltip title="Pin to Dashboard" arrow>
            <IconButton
              size="small"
              onClick={() => onPinToBoard('DIAGNOSTIC', { diagnostic }, sourceQuery)}
              sx={{
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 0,
                p: 0.5,
                ml: 1,
                '&:hover': {
                  color: '#22c55e',
                  borderColor: '#22c55e',
                  bgcolor: 'rgba(34, 197, 94, 0.08)',
                },
              }}
            >
              <PushPinIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
        )}
      </Box>

      {/* Next Move / Suggested Resolution */}
      <NextMoveBox nextMove={nextMove} colors={colors} />

      {/* Diagnostic items list */}
      <Box sx={{ mt: 1 }}>
        {diagnostic.items?.map((item) => (
          <DiagnosticItem
            key={item.itemId}
            item={item}
            colors={colors}
            onRuleAction={handleRuleAction}
            onRelatedEntityClick={handleRelatedEntityClick}
            defaultExpanded={item.status === 'FAILED' || item.status === 'WARNING'}
          />
        ))}
      </Box>

      {/* Additional diagnostic-level actions */}
      {diagnostic.actions && diagnostic.actions.length > 2 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {diagnostic.actions.slice(2).map((action) => (
            <Button
              key={action.actionId}
              variant="outlined"
              size="small"
              onClick={() => handleDiagnosticAction(action)}
              sx={{
                borderColor: '#22c55e',
                color: '#22c55e',
                borderRadius: 0,
                '&:hover': {
                  borderColor: '#16a34a',
                  bgcolor: 'rgba(34, 197, 94, 0.08)',
                },
              }}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DiagnosticRenderer;
